import { t } from "i18next";
import { Typography, IconButton } from "@material-tailwind/react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { Table } from "../../../components/table/Table";
import { OverflowTooltip } from "../../../components/table/TableCellTooltip";
import { getFleetNumber, getHourMeter } from "../../../services/machine/machine";
import EditCreateDrawer from "../../../components/edit-create-drawer/EditCreateDrawer";
import MachineForm from "../../../components/data/machines/MachineForm";

interface MachinesTableProps {
  machines: EasyTech.Machine[];
  onHeadClick: (head: string, orderDesc: boolean) => void;
  orderDesc: boolean;
  onEdit: (machine: EasyTech.Machine) => Promise<void>;
  onDelete: (machine: EasyTech.Machine) => void;
  onRowClick: (machine: EasyTech.Machine) => void;
  customerId?: string;
}

export function MachinesTable({
  machines,
  onHeadClick,
  orderDesc,
  onEdit,
  onDelete,
  onRowClick,
  customerId
}: MachinesTableProps) {
  const tableHead = [
    { label: t("client:ref"), orderKey: "ref", width: "10%" },
    { label: t("client:serialNumber"), orderKey: "serialNumber", width: "10%" },
    { label: t("machine:releaseYear"), orderKey: "releaseYear", width: "10%" },
    { label: t("common:type"), orderKey: "type.label", width: "20%" },
    { label: t("client:brand"), orderKey: "brand.label", width: "20%" },
    { label: t("machine:form:fields:hourMeter:label"), orderKey: "hourMeter", width: "10%" },
    { label: t("machine:form:fields:fleetNumber:label"), orderKey: "fleetIdentifier", width: "10%" },
    { label: t("common:actions"), orderKey: null, width: "10%" }
  ];

  return (
    <Table
      tableHead={tableHead}
      emptyTable={machines.length === 0 && t("machine:noData")}
      onHeadClick={onHeadClick}
      orderDesc={orderDesc}
      enableRowLinks
    >
      {machines.map((machine, index) => {
        const isLast = index === machines.length - 1;
        const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";
        const machineUrl = customerId ? `/customers/${customerId}/machines/${machine.id}` : "";

        return (
          <tr 
            key={machine.id || index}
            onClick={() => onRowClick(machine)}
            className="cursor-pointer hover:bg-gray-50"
            data-href={machineUrl}
          >
            <td className={classes}>
              <OverflowTooltip content={machine.ref || "-"} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {machine.ref || "-"}
                </Typography>
              </OverflowTooltip>
            </td>
            <td className={classes}>
              <OverflowTooltip content={machine.serialNumber || "-"} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {machine.serialNumber || "-"}
                </Typography>
              </OverflowTooltip>
            </td>
            <td className={classes}>
              <OverflowTooltip content={String(machine.releaseYear || "-")} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {machine.releaseYear || "-"}
                </Typography>
              </OverflowTooltip>
            </td>
            <td className={classes}>
              <OverflowTooltip content={machine.type?.label || "-"} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {machine.type?.label || "-"}
                </Typography>
              </OverflowTooltip>
            </td>
            <td className={classes}>
              <OverflowTooltip content={machine.brand?.label || "-"} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {machine.brand?.label || "-"}
                </Typography>
              </OverflowTooltip>
            </td>
            <td className={classes}>
              <OverflowTooltip content={String(getHourMeter(machine) || "-")} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {getHourMeter(machine) || "-"}
                </Typography>
              </OverflowTooltip>
            </td>
            <td className={classes}>
              <OverflowTooltip content={String(getFleetNumber(machine) || "-")} className="et-cell-truncate">
                <Typography variant="small" color="blue-gray">
                  {getFleetNumber(machine) || "-"}
                </Typography>
              </OverflowTooltip>
            </td>
            <td className={classes} data-no-link> 
              <div className="et-cell-actions">
                <div onClick={(e) => e.stopPropagation()}>
                  <EditCreateDrawer<EasyTech.Machine>
                    entityName="Machine"
                    data={machine}
                    FormComponent={MachineForm}
                    onSubmit={onEdit}
                    isNotEditable={false}
                    isNotCreatable={true}
                  />
                </div>
                <IconButton 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(machine);
                  }} 
                  variant="text"
                  className="icon-btn" 
                  size="sm"
                >
                  <TrashIcon className="w-[14px]" />
                </IconButton>
              </div>
            </td>
          </tr>
        );
      })}
    </Table>
  );
}
